import * as THREE from 'three';

export interface ModifierUniforms {
  [key: string]: THREE.IUniform;
}

export interface GpuModifierDef {
  type: string;
  enabled: boolean;
  params: Record<string, number | boolean | string>;
}

// Simplex noise GLSL (compact 3D version for Noise modifier)
const NOISE_GLSL = /* glsl */ `
vec3 mod289_v(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289_v4(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute_v(vec4 x) { return mod289_v4(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt_v(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise3d(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289_v(i);
  vec4 p = permute_v(permute_v(permute_v(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt_v(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}
`;

function axisIndex(axis: string): number {
  return axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
}

function buildBendGLSL(prefix: string): string {
  return /* glsl */ `
    {
      float bendAngle = ${prefix}_angle * 3.14159265 / 180.0;
      float bendDir   = ${prefix}_direction * 3.14159265 / 180.0;
      int   bendAxis  = ${prefix}_axis;
      float upperLim  = ${prefix}_upperLimit;
      float lowerLim  = ${prefix}_lowerLimit;
      bool  limOn     = ${prefix}_limitEnabled > 0.5;

      float h;
      if (bendAxis == 0)      h = transformed.x;
      else if (bendAxis == 1) h = transformed.y;
      else                    h = transformed.z;

      float t = 1.0;
      if (limOn) {
        float span = upperLim - lowerLim;
        if (span > 0.0001) t = clamp((h - lowerLim) / span, 0.0, 1.0);
        else t = 0.0;
      }

      float angle = bendAngle * t;
      float cosA = cos(angle);
      float sinA = sin(angle);
      float cosD = cos(bendDir);
      float sinD = sin(bendDir);

      if (bendAxis == 2) {
        float rx = transformed.x * cosD - transformed.y * sinD;
        float ry = transformed.x * sinD + transformed.y * cosD;
        float newRx = rx * cosA - transformed.z * sinA;
        float newZ  = rx * sinA + transformed.z * cosA;
        transformed.x = newRx * cosD + ry * sinD;
        transformed.y = -newRx * sinD + ry * cosD;
        transformed.z = newZ;
      } else if (bendAxis == 1) {
        float rx = transformed.x * cosD - transformed.z * sinD;
        float rz = transformed.x * sinD + transformed.z * cosD;
        float newRx = rx * cosA - transformed.y * sinA;
        float newY  = rx * sinA + transformed.y * cosA;
        transformed.x = newRx * cosD + rz * sinD;
        transformed.z = -newRx * sinD + rz * cosD;
        transformed.y = newY;
      } else {
        float ry = transformed.y * cosD - transformed.z * sinD;
        float rz = transformed.y * sinD + transformed.z * cosD;
        float newRy = ry * cosA - transformed.x * sinA;
        float newX  = ry * sinA + transformed.x * cosA;
        transformed.y = newRy * cosD + rz * sinD;
        transformed.z = -newRy * sinD + rz * cosD;
        transformed.x = newX;
      }
    }
  `;
}

function buildTwistGLSL(prefix: string): string {
  return /* glsl */ `
    {
      float twistAngle = ${prefix}_angle * 3.14159265 / 180.0;
      int   twistAxis  = ${prefix}_axis;
      float twistBias  = ${prefix}_bias;

      float h;
      if (twistAxis == 0)      h = transformed.x;
      else if (twistAxis == 1) h = transformed.y;
      else                     h = transformed.z;

      float t = h + twistBias;
      float angle = twistAngle * t;
      float c = cos(angle);
      float s = sin(angle);

      if (twistAxis == 2) {
        float nx = transformed.x * c - transformed.y * s;
        float ny = transformed.x * s + transformed.y * c;
        transformed.x = nx;
        transformed.y = ny;
      } else if (twistAxis == 1) {
        float nx = transformed.x * c - transformed.z * s;
        float nz = transformed.x * s + transformed.z * c;
        transformed.x = nx;
        transformed.z = nz;
      } else {
        float ny = transformed.y * c - transformed.z * s;
        float nz = transformed.y * s + transformed.z * c;
        transformed.y = ny;
        transformed.z = nz;
      }
    }
  `;
}

function buildTaperGLSL(prefix: string): string {
  return /* glsl */ `
    {
      float taperAmt  = ${prefix}_amount;
      float taperCurv = ${prefix}_curve;
      int   taperAxis = ${prefix}_axis;

      float h;
      if (taperAxis == 0)      h = transformed.x;
      else if (taperAxis == 1) h = transformed.y;
      else                     h = transformed.z;

      float t = h * 0.5 + 0.5;
      float factor = 1.0 + taperAmt * pow(t, 1.0 + taperCurv);

      if (taperAxis == 0) {
        transformed.y *= factor;
        transformed.z *= factor;
      } else if (taperAxis == 1) {
        transformed.x *= factor;
        transformed.z *= factor;
      } else {
        transformed.x *= factor;
        transformed.y *= factor;
      }
    }
  `;
}

function buildNoiseGLSL(prefix: string): string {
  return /* glsl */ `
    {
      float noiseStr   = ${prefix}_strength;
      float noiseScale = ${prefix}_scale;
      float noiseSeed  = ${prefix}_seed;
      float noiseAnim  = ${prefix}_animated;

      vec3 noiseCoord = transformed * noiseScale + vec3(noiseSeed);
      if (noiseAnim > 0.5) noiseCoord += vec3(0.0, 0.0, ${prefix}_time * 0.5);

      float nx = snoise3d(noiseCoord);
      float ny = snoise3d(noiseCoord + vec3(31.416, 0.0, 0.0));
      float nz = snoise3d(noiseCoord + vec3(0.0, 47.853, 0.0));

      transformed += vec3(nx, ny, nz) * noiseStr;
    }
  `;
}

export interface CompiledShaderResult {
  vertexChunk: string;
  uniforms: ModifierUniforms;
  needsNoise: boolean;
  needsTime: boolean;
}

export function compileModifierShader(modifiers: GpuModifierDef[]): CompiledShaderResult | null {
  const gpuMods = modifiers.filter((m) => m.enabled && isGpuModifier(m.type));
  if (gpuMods.length === 0) return null;

  const uniformDecls: string[] = [];
  const bodyChunks: string[] = [];
  const uniforms: ModifierUniforms = {};
  let needsNoise = false;
  let needsTime = false;

  gpuMods.forEach((mod, i) => {
    const prefix = `u_mod${i}`;

    switch (mod.type) {
      case 'bend': {
        const p = mod.params;
        addUniform(prefix, 'angle', p.angle as number, uniforms, uniformDecls);
        addUniform(prefix, 'direction', p.direction as number, uniforms, uniformDecls);
        addUniformInt(prefix, 'axis', axisIndex(p.axis as string), uniforms, uniformDecls);
        addUniform(prefix, 'upperLimit', p.upperLimit as number, uniforms, uniformDecls);
        addUniform(prefix, 'lowerLimit', p.lowerLimit as number, uniforms, uniformDecls);
        addUniform(prefix, 'limitEnabled', (p.limitEnabled as boolean) ? 1.0 : 0.0, uniforms, uniformDecls);
        bodyChunks.push(buildBendGLSL(prefix));
        break;
      }
      case 'twist': {
        const p = mod.params;
        addUniform(prefix, 'angle', p.angle as number, uniforms, uniformDecls);
        addUniformInt(prefix, 'axis', axisIndex(p.axis as string), uniforms, uniformDecls);
        addUniform(prefix, 'bias', p.bias as number, uniforms, uniformDecls);
        bodyChunks.push(buildTwistGLSL(prefix));
        break;
      }
      case 'taper': {
        const p = mod.params;
        addUniform(prefix, 'amount', p.amount as number, uniforms, uniformDecls);
        addUniform(prefix, 'curve', p.curve as number, uniforms, uniformDecls);
        addUniformInt(prefix, 'axis', axisIndex(p.axis as string), uniforms, uniformDecls);
        bodyChunks.push(buildTaperGLSL(prefix));
        break;
      }
      case 'noise': {
        const p = mod.params;
        needsNoise = true;
        if (p.animated) needsTime = true;
        addUniform(prefix, 'strength', p.strength as number, uniforms, uniformDecls);
        addUniform(prefix, 'scale', p.scale as number, uniforms, uniformDecls);
        addUniform(prefix, 'seed', p.seed as number, uniforms, uniformDecls);
        addUniform(prefix, 'animated', (p.animated as boolean) ? 1.0 : 0.0, uniforms, uniformDecls);
        addUniform(prefix, 'time', 0, uniforms, uniformDecls);
        bodyChunks.push(buildNoiseGLSL(prefix));
        break;
      }
    }
  });

  const vertexChunk = [
    ...uniformDecls,
    needsNoise ? NOISE_GLSL : '',
    'void modifyVertex(inout vec3 transformed) {',
    ...bodyChunks,
    '}',
  ].join('\n');

  return { vertexChunk, uniforms, needsNoise, needsTime };
}

export function updateModifierUniforms(
  uniforms: ModifierUniforms,
  modifiers: GpuModifierDef[],
  time: number,
): void {
  const gpuMods = modifiers.filter((m) => m.enabled && isGpuModifier(m.type));

  gpuMods.forEach((mod, i) => {
    const prefix = `u_mod${i}`;
    const p = mod.params;

    switch (mod.type) {
      case 'bend':
        setU(uniforms, prefix, 'angle', p.angle as number);
        setU(uniforms, prefix, 'direction', p.direction as number);
        setUInt(uniforms, prefix, 'axis', axisIndex(p.axis as string));
        setU(uniforms, prefix, 'upperLimit', p.upperLimit as number);
        setU(uniforms, prefix, 'lowerLimit', p.lowerLimit as number);
        setU(uniforms, prefix, 'limitEnabled', (p.limitEnabled as boolean) ? 1.0 : 0.0);
        break;
      case 'twist':
        setU(uniforms, prefix, 'angle', p.angle as number);
        setUInt(uniforms, prefix, 'axis', axisIndex(p.axis as string));
        setU(uniforms, prefix, 'bias', p.bias as number);
        break;
      case 'taper':
        setU(uniforms, prefix, 'amount', p.amount as number);
        setU(uniforms, prefix, 'curve', p.curve as number);
        setUInt(uniforms, prefix, 'axis', axisIndex(p.axis as string));
        break;
      case 'noise':
        setU(uniforms, prefix, 'strength', p.strength as number);
        setU(uniforms, prefix, 'scale', p.scale as number);
        setU(uniforms, prefix, 'seed', p.seed as number);
        setU(uniforms, prefix, 'animated', (p.animated as boolean) ? 1.0 : 0.0);
        setU(uniforms, prefix, 'time', time);
        break;
    }
  });
}

function isGpuModifier(type: string): boolean {
  return type === 'bend' || type === 'twist' || type === 'taper' || type === 'noise';
}

function addUniform(prefix: string, name: string, value: number, uniforms: ModifierUniforms, decls: string[]) {
  const key = `${prefix}_${name}`;
  uniforms[key] = { value };
  decls.push(`uniform float ${key};`);
}

function addUniformInt(prefix: string, name: string, value: number, uniforms: ModifierUniforms, decls: string[]) {
  const key = `${prefix}_${name}`;
  uniforms[key] = { value };
  decls.push(`uniform int ${key};`);
}

function setU(uniforms: ModifierUniforms, prefix: string, name: string, value: number) {
  const key = `${prefix}_${name}`;
  if (uniforms[key]) uniforms[key].value = value;
}

function setUInt(uniforms: ModifierUniforms, prefix: string, name: string, value: number) {
  const key = `${prefix}_${name}`;
  if (uniforms[key]) uniforms[key].value = value;
}
